import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Fade,
  Chip,
  Pagination,
  Stack,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Snackbar,
  useTheme,
  useMediaQuery,
  Hidden,
  Menu,
  Autocomplete,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Groups,
  Search,
  LocationOn,
  Phone,
  Email,
  Language,
  Assignment,
  ArrowBack,
  Visibility,
  History,
  CheckCircle,
  MoreVert,
} from "@mui/icons-material";
import {
  fetchAllChitalishta,
  createChitalishte,
  updateChitalishte,
  deleteChitalishte,
  clearError,
  selectAllChitalishta,
  selectChitalishtaLoading,
  selectChitalishtaError,
} from "../../../store/slices/chitalishteAdminSlice";
import { updateFeedbackStatus } from "../../../store/slices/adminFeedbackSlice";
import TabPanel from "../shared/TabPanel";
import ChitalishteForm from "../chitalishte/ChitalishteForm";
import InformationCardsManager from "../information-cards/InformationCardsManager";
import ChitalishteAuditHistory from "../chitalishte/ChitalishteAuditHistory";
import ChitalishteInformationCardsPreview from "../information-cards/ChitalishteInformationCardsPreview";
import { LibraryBooks } from "@mui/icons-material";

import {
  fetchLibrariesByChitalishteId,
  selectLibraries,
  clearLibraries,
  createLibrary,
  updateLibrary,
} from "../../../store/slices/libraryAdminSlice";
import LibraryForm from "../chitalishte/LibraryForm";
import {
  fetchAllMunicipalities,
  selectAllMunicipalities,
  selectMunicipalitiesLoading,
  selectAllTowns,
  selectTownsLoading,
  fetchAllTowns,
} from "../../../store/slices/chitalishtaSlice";

const REGIONS = [
  "Благоевград",
  "Бургас",
  "Варна",
  "Велико Търново",
  "Видин",
  "Враца",
  "Габрово",
  "Добрич",
  "Кърджали",
  "Кюстендил",
  "Ловеч",
  "Монтана",
  "Пазарджик",
  "Перник",
  "Плевен",
  "Пловдив",
  "Разград",
  "Русе",
  "Силистра",
  "Сливен",
  "Смолян",
  "София",
  "София-град",
  "Стара Загора",
  "Търговище",
  "Хасково",
  "Шумен",
  "Ямбол",
];

// Main ChitalishteAdmin Component
const ChitalishteAdmin = ({ dbAction, setDbAction }) => {
  const dispatch = useDispatch();
  const allChitalishta = useSelector(selectAllChitalishta);
  const loading = useSelector(selectChitalishtaLoading);
  const error = useSelector(selectChitalishtaError);
  const municipalities = useSelector(selectAllMunicipalities);
  const municipalitiesLoading = useSelector(selectMunicipalitiesLoading);
  const towns = useSelector(selectAllTowns);
  const townsLoading = useSelector(selectTownsLoading);

  const [formOpen, setFormOpen] = useState(false);
  const [selectedChitalishte, setSelectedChitalishte] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chitalishteToDelete, setChitalishteToDelete] = useState(null);
  const [selectedChitalishteForCards, setSelectedChitalishteForCards] =
    useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [tabValue, setTabValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [municipalityFilter, setMunicipalityFilter] = useState("");
  const [townFilter, setTownFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteFeedbackId, setDeleteFeedbackId] = useState("");
  const [feedbackId, setFeedbackId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isUpdatingFeedback, setIsUpdatingFeedback] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedChitalishteForMenu, setSelectedChitalishteForMenu] =
    useState(null);
  const [libraryFormOpen, setLibraryFormOpen] = useState(false);
  const [selectedChitalishteForLibrary, setSelectedChitalishteForLibrary] =
    useState(null);
  const libraries = useSelector(selectLibraries);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (dbAction && dbAction.type === "editLibrary") {
      handleEditLibrary(dbAction.chitalishte, dbAction.feedbackId);
      setDbAction(null);
    }
  }, [dbAction, setDbAction]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const feedbackIdFromUrl = params.get("feedbackId");
    if (feedbackIdFromUrl) {
      setFeedbackId(feedbackIdFromUrl);
    }
    dispatch(fetchAllChitalishta({ page: 0, size: 5000 }));
    dispatch(fetchAllMunicipalities());
    dispatch(fetchAllTowns());
  }, [dispatch]);

  const handleUpdateFeedbackStatus = async (feedbackId, status, adminNotes) => {
    if (!feedbackId) {
      setSnackbar({
        open: true,
        message: "Невалиден Feedback ID",
        severity: "error",
      });
      return false;
    }

    try {
      setIsUpdatingFeedback(true);
      await dispatch(
        updateFeedbackStatus({
          id: feedbackId,
          status: status,
          processedBy: "admin",
          notes: adminNotes,
        }),
      ).unwrap();
      setSnackbar({
        open: true,
        message:
          "Статусът на обратната връзка е обновен успешно на 'Обработен'",
        severity: "success",
      });
      return true;
    } catch (error) {
      console.error("Failed to update feedback status:", error);
      let errorMessage = "Неуспешно обновяване на статуса на обратната връзка";
      if (error.message) {
        if (error.message.includes("404")) {
          errorMessage = "Фидбакът не беше намерен (404)";
        } else if (error.message.includes("400")) {
          errorMessage = "Невалидни данни за фийдбака";
        } else if (error.message.includes("403")) {
          errorMessage = "Нямате права да обновите фийдбака";
        }
      }
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
      throw error; // Re-throw to be caught by the main handleSubmit
    } finally {
      setIsUpdatingFeedback(false);
    }
  };

  const handleMenuClick = (event, chitalishte) => {
    setAnchorEl(event.currentTarget);
    setSelectedChitalishteForMenu(chitalishte);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedChitalishteForMenu(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Филтриране на читалища
  const filteredChitalishta = allChitalishta.filter((chitalishte) => {
    const matchesSearch =
      searchTerm === "" ||
      chitalishte.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chitalishte.town.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (chitalishte.municipality &&
        chitalishte.municipality
          .toLowerCase()
          .includes(searchTerm.toLowerCase()));

    const matchesRegion =
      regionFilter === "" || chitalishte.region?.toLowerCase() === regionFilter?.toLowerCase();
    const matchesMunicipality =
      municipalityFilter === "" ||
      chitalishte.municipality === municipalityFilter;
    const matchesTown = townFilter === "" || chitalishte.town === townFilter;
    const matchesStatus =
      statusFilter === "" || chitalishte.status === statusFilter;

    return (
      matchesSearch &&
      matchesRegion &&
      matchesMunicipality &&
      matchesTown &&
      matchesStatus
    );
  });

  // Пагинация
  const totalPages = Math.ceil(filteredChitalishta.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedChitalishta = filteredChitalishta.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleCreate = () => {
    setSelectedChitalishte(null);
    setFormOpen(true);
  };

  const handleEdit = (chitalishte) => {
    setSelectedChitalishte(chitalishte);
    setFormOpen(true);
  };

  const handleDelete = (chitalishte) => {
    setChitalishteToDelete(chitalishte);
    setDeleteDialogOpen(true);
  };

  const handleInformationCards = (chitalishte) => {
    setSelectedChitalishteForCards(chitalishte);
  };

  const handleEditLibrary = (chitalishte, feedbackId) => {
    if (feedbackId) {
      setFeedbackId(feedbackId);
    }
    dispatch(fetchLibrariesByChitalishteId(chitalishte.id));
    setSelectedChitalishteForLibrary(chitalishte);
    setLibraryFormOpen(true);
  };

  const handleBackFromCards = () => {
    setSelectedChitalishteForCards(null);
  };

  const handleViewDetails = (chitalishte) => {
    dispatch(fetchLibrariesByChitalishteId(chitalishte.id));
    setSelectedChitalishte(chitalishte);
    setViewMode("detail");
    setTabValue(0);
  };

  const handleBackToList = () => {
    setSelectedChitalishte(null);
    setViewMode("list");
  };

  const handleOpenLibraryFormFromChitalishte = (chitalishte, feedbackId) => {
    setFormOpen(false); // Close chitalishte form
    setSelectedChitalishteForLibrary(chitalishte);
    setFeedbackId(feedbackId); // Set the feedbackId for LibraryForm
    dispatch(fetchLibrariesByChitalishteId(chitalishte.id));
    setLibraryFormOpen(true);
  };

  const handleSubmit = async (formData) => {
    try {
      const { shouldUpdateFeedback, feedbackId, ...chitalishteData } = formData;
      const feedbackIdToSend =
        feedbackId && feedbackId.trim() ? feedbackId.trim() : null;

      if (selectedChitalishte) {
        await dispatch(
          updateChitalishte({
            id: selectedChitalishte.id,
            data: chitalishteData,
            feedbackId: feedbackIdToSend,
          }),
        ).unwrap();
        setSnackbar({
          open: true,
          message: "Читалището е обновено успешно!",
          severity: "success",
        });

        if (shouldUpdateFeedback && feedbackIdToSend) {
          await handleUpdateFeedbackStatus(
            feedbackIdToSend,
            "PROCESSED",
            "Автоматично обработен при редакция на читалището",
          );
        }
      } else {
        await dispatch(
          createChitalishte({
            data: chitalishteData,
            feedbackId: feedbackIdToSend,
          }),
        ).unwrap();
        setSnackbar({
          open: true,
          message: "Читалището е създадено успешно!",
          severity: "success",
        });

        if (shouldUpdateFeedback && feedbackIdToSend) {
          await handleUpdateFeedbackStatus(
            feedbackIdToSend,
            "PROCESSED",
            "Автоматично обработен при създаване на читалището",
          );
        }
      }

      setFormOpen(false);
      dispatch(fetchAllChitalishta({ page: 0, size: 5000 }));
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      const errorMessage = error.message || "Възникна грешка при записа.";
      setSnackbar({
        open: true,
        message: `Грешка: ${errorMessage}`,
        severity: "error",
      });
    }
  };

  const handleLibrarySubmit = async (formData) => {
    try {
      const { shouldUpdateFeedback, feedbackId, ...libraryData } = formData;
      const feedbackIdToSend =
        feedbackId && feedbackId.trim() ? feedbackId.trim() : null;

      if (libraries && libraries.length > 0) {
        await dispatch(
          updateLibrary({
            id: libraries[0].id,
            libraryData: { ...libraryData, feedbackId: feedbackIdToSend },
          }),
        ).unwrap();
        setSnackbar({
          open: true,
          message: "Библиотеката е обновена успешно!",
          severity: "success",
        });

        if (shouldUpdateFeedback && feedbackIdToSend) {
          await handleUpdateFeedbackStatus(
            feedbackIdToSend,
            "PROCESSED",
            "Автоматично обработен при редакция на библиотеката",
          );
        }
      } else {
        await dispatch(
          createLibrary({
            libraryData: { ...libraryData, feedbackId: feedbackIdToSend },
          }),
        ).unwrap();
        setSnackbar({
          open: true,
          message: "Библиотеката е създадена успешно!",
          severity: "success",
        });

        if (shouldUpdateFeedback && feedbackIdToSend) {
          await handleUpdateFeedbackStatus(
            feedbackIdToSend,
            "PROCESSED",
            "Автоматично обработен при създаване на библиотеката",
          );
        }
      }

      setLibraryFormOpen(false);
      dispatch(fetchLibrariesByChitalishteId(selectedChitalishteForLibrary.id));
    } catch (error) {
      console.error("Error in handleLibrarySubmit:", error);
      const errorMessage = error.message || "Възникна грешка при записа.";
      setSnackbar({
        open: true,
        message: `Грешка: ${errorMessage}`,
        severity: "error",
      });
    }
  };

  const confirmDelete = () => {
    if (chitalishteToDelete) {
      const feedbackIdToSend = deleteFeedbackId.trim() || null;
      dispatch(
        deleteChitalishte({
          id: chitalishteToDelete.id,
          feedbackId: feedbackIdToSend,
        }),
      ).then(() => {
        setDeleteDialogOpen(false);
        setChitalishteToDelete(null);
        setDeleteFeedbackId("");
        dispatch(fetchAllChitalishta({ page: 0, size: 5000 }));
        if (
          selectedChitalishte &&
          selectedChitalishte.id === chitalishteToDelete.id
        ) {
          handleBackToList();
        }
      });
    }
  };

  const handleRefresh = () => {
    dispatch(fetchAllChitalishta({ page: 0, size: 5000 }));
    setSearchTerm("");
    setRegionFilter("");
    setMunicipalityFilter("");
    setTownFilter("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "INACTIVE":
        return "warning";
      case "CLOSED":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "ACTIVE":
        return "Действащо";
      case "INACTIVE":
        return "Неактивно";
      case "CLOSED":
        return "Закрито";
      default:
        return status;
    }
  };

  const handleCopyId = (chitalishteId) => {
    navigator.clipboard.writeText(chitalishteId.toString());
    // console.log("Chitalishte ID copied:", chitalishteId);
  };

  if (selectedChitalishteForCards) {
    return (
      <InformationCardsManager
        chitalishte={selectedChitalishteForCards}
        onBack={handleBackFromCards}
      />
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          component="h2"
          sx={{ fontWeight: 700 }}
        >
          Управление на Читалища
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <Button
            startIcon={<Refresh />}
            onClick={handleRefresh}
            variant="outlined"
            fullWidth={isMobile}
          >
            Обнови
          </Button>
          {viewMode === "list" && (
            <Button
              startIcon={<Add />}
              onClick={handleCreate}
              variant="contained"
              fullWidth={isMobile}
            >
              Добави
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => dispatch(clearError())}
        >
          {error}
        </Alert>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          icon={snackbar.severity === "success" ? <CheckCircle /> : undefined}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
          {isUpdatingFeedback && " (обновяване...)"}
        </Alert>
      </Snackbar>

      {viewMode === "detail" && selectedChitalishte && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton onClick={handleBackToList}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h5">{selectedChitalishte.name}</Typography>
              <Chip
                label={getStatusText(selectedChitalishte.status)}
                color={getStatusColor(selectedChitalishte.status)}
                size="small"
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => handleEdit(selectedChitalishte)}
              >
                Редактирай
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => handleDelete(selectedChitalishte)}
              >
                Изтрий
              </Button>
            </Stack>
          </Stack>

          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          >
            <Tab label="Основна информация" />
            <Tab label="Библиотеки" />
            <Tab label="Информационни карти" />
            <Tab
              label="История на промените"
              icon={<History />}
              iconPosition="start"
            />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Регистрационен номер
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedChitalishte.registrationNumber || "Няма данни"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  БУЛСТАТ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedChitalishte.bulstat || "Няма данни"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Председател
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedChitalishte.chairman || "Няма данни"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Секретар
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedChitalishte.secretary || "Няма данни"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Населено място
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedChitalishte.town || "Няма данни"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Община
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedChitalishte.municipality || "Няма данни"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Област
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedChitalishte.region || "Няма данни"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Адрес
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedChitalishte.address || "Няма данни"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Телефон
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedChitalishte.phone || "Няма данни"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Имейл
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedChitalishte.email || "Няма данни"}
                </Typography>
              </Grid>

              {selectedChitalishte.urlToLibrariesSite && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Уебсайт
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <a
                      href={selectedChitalishte.urlToLibrariesSite}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedChitalishte.urlToLibrariesSite}
                    </a>
                  </Typography>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {libraries && libraries.length > 0 ? (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Име
                    </Typography>
                    <Typography variant="body1">{libraries[0].name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Регистрационен номер
                    </Typography>
                    <Typography variant="body1">
                      {libraries[0].registrationNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Адрес
                    </Typography>
                    <Typography variant="body1">
                      {libraries[0].address}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      БУЛСТАТ
                    </Typography>
                    <Typography variant="body1">
                      {libraries[0].bulstat}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Населено място
                    </Typography>
                    <Typography variant="body1">{libraries[0].town}</Typography>
                  </Grid>
                  {/* Add other library fields here */}
                </Grid>
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={() => handleEditLibrary(selectedChitalishte)}
                  sx={{ mt: 2 }}
                >
                  Редактирай Библиотека
                </Button>
              </Box>
            ) : (
              <Typography>Няма данни за библиотека.</Typography>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <ChitalishteInformationCardsPreview
              chitalishteId={selectedChitalishte.id}
              chitalishteName={selectedChitalishte.name}
              onAddCard={() =>
                setSelectedChitalishteForCards(selectedChitalishte)
              }
              onViewAll={() =>
                setSelectedChitalishteForCards(selectedChitalishte)
              }
            />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <ChitalishteAuditHistory
              chitalishteId={selectedChitalishte.id}
              chitalishteName={selectedChitalishte.name}
            />
          </TabPanel>
        </Paper>
      )}

      {viewMode === "list" && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid
              container
              spacing={1}
              alignItems="center"
              // justifyContent="space-between"
            >
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  // label="Търсене по име, населено място или община"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Име, населено място, община..."
                />
              </Grid>

               <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  fullWidth
                  options={REGIONS}
                  value={regionFilter || null}
                  onChange={(event, newValue) => {
                    setRegionFilter(newValue || "");
                    setCurrentPage(1);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Област"
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  fullWidth
                  options={municipalities}
                  value={municipalityFilter || null}
                  onChange={(event, newValue) => {
                    setMunicipalityFilter(newValue || "");
                    setCurrentPage(1);
                  }}
                  loading={municipalitiesLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Община"
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  fullWidth
                  options={towns}
                  value={townFilter || null}
                  onChange={(event, newValue) => {
                    setTownFilter(newValue || "");
                    setCurrentPage(1);
                  }}
                  loading={townsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Населено място"
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Брой на страница</InputLabel>
                  <Select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    label="Брой на страница"
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {(searchTerm ||
              regionFilter ||
              municipalityFilter ||
              townFilter ||
              statusFilter) && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Активни филтри:
                </Typography>
                {searchTerm && (
                  <Chip
                    label={`Търсене: "${searchTerm}"`}
                    size="small"
                    onDelete={() => {
                      setSearchTerm("");
                      setCurrentPage(1);
                    }}
                  />
                )}
                {regionFilter && (
                  <Chip
                    label={`Област: ${regionFilter}`}
                    size="small"
                    onDelete={() => {
                      setRegionFilter("");
                      setCurrentPage(1);
                    }}
                  />
                )}
                {municipalityFilter && (
                  <Chip
                    label={`Община: ${municipalityFilter}`}
                    size="small"
                    onDelete={() => {
                      setMunicipalityFilter("");
                      setCurrentPage(1);
                    }}
                  />
                )}
                {townFilter && (
                  <Chip
                    label={`Населено място: ${townFilter}`}
                    size="small"
                    onDelete={() => {
                      setTownFilter("");
                      setCurrentPage(1);
                    }}
                  />
                )}
                {statusFilter && (
                  <Chip
                    label={`Статус: ${getStatusText(statusFilter)}`}
                    size="small"
                    onDelete={() => {
                      setStatusFilter("");
                      setCurrentPage(1);
                    }}
                  />
                )}
                <Button
                  size="small"
                  onClick={() => {
                    setSearchTerm("");
                    setRegionFilter("");
                    setMunicipalityFilter("");
                    setTownFilter("");
                    setStatusFilter("");
                    setCurrentPage(1);
                  }}
                >
                  Изчисти всички
                </Button>
              </Box>
            )}
          </Paper>

          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Показване {startIndex + 1}-
              {Math.min(startIndex + itemsPerPage, filteredChitalishta.length)}{" "}
              от {filteredChitalishta.length} читалища
              {filteredChitalishta.length !== allChitalishta.length &&
                ` (от общо ${allChitalishta.length})`}
            </Typography>
            {loading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Зареждане...
                </Typography>
              </Box>
            )}
          </Box>

          <Box>
            <TableContainer component={Paper} elevation={3}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "primary.main" }}>
                    <TableCell
                      sx={{ color: "white", fontWeight: "bold", width: "60px" }}
                    >
                      ID
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Име
                    </TableCell>
                    <Hidden smDown>
                      <TableCell
                        sx={{
                          color: "white",
                          fontWeight: "bold",
                          width: "120px",
                        }}
                      >
                        Населено място
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "white",
                          fontWeight: "bold",
                          width: "120px",
                        }}
                      >
                        Община
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "white",
                          fontWeight: "bold",
                          width: "120px",
                        }}
                      >
                        Област
                      </TableCell>
                    </Hidden>
                    <TableCell
                      sx={{
                        color: "white",
                        fontWeight: "bold",
                        width: "100px",
                      }}
                    >
                      Статус
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "white",
                        fontWeight: "bold",
                        width: "180px",
                        textAlign: "center",
                      }}
                    >
                      Действия
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedChitalishta.map((chitalishte) => (
                    <Fade in={true} key={chitalishte.id}>
                      <TableRow hover>
                        <TableCell>
                          <Tooltip title="Копирай ID">
                            <Chip
                              label={`#${chitalishte.id}`}
                              size="small"
                              variant="outlined"
                              onClick={() => handleCopyId(chitalishte.id)}
                              sx={{ cursor: "pointer" }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                mb: 0.5,
                                fontSize: isMobile ? "0.8rem" : "1rem",
                              }}
                            >
                              {chitalishte.name}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                mt: 0.5,
                              }}
                            >
                              <Hidden smDown>
                                {chitalishte.phone && (
                                  <Tooltip title="Телефон">
                                    <Chip
                                      icon={<Phone sx={{ fontSize: 14 }} />}
                                      label={chitalishte.phone}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                    />
                                  </Tooltip>
                                )}
                                {chitalishte.email && (
                                  <Tooltip title="Имейл">
                                    <Chip
                                      icon={<Email sx={{ fontSize: 14 }} />}
                                      label={chitalishte.email}
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                    />
                                  </Tooltip>
                                )}
                                {chitalishte.chitalishtaUrl && (
                                  <Tooltip title="Уебсайт">
                                    <Chip
                                      icon={<Language sx={{ fontSize: 14 }} />}
                                      label="Website"
                                      size="small"
                                      variant="outlined"
                                      color="info"
                                      onClick={() =>
                                        window.open(
                                          chitalishte.chitalishtaUrl,
                                          "_blank",
                                        )
                                      }
                                      sx={{ cursor: "pointer" }}
                                    />
                                  </Tooltip>
                                )}
                              </Hidden>
                            </Box>
                          </Box>
                        </TableCell>
                        <Hidden smDown>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <LocationOn
                                sx={{
                                  fontSize: 16,
                                  mr: 0.5,
                                  color: "text.secondary",
                                }}
                              />
                              <Typography variant="body2">
                                {chitalishte.town}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {chitalishte.municipality}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={chitalishte.region}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          </TableCell>
                        </Hidden>
                        <TableCell>
                          <Chip
                            label={getStatusText(chitalishte.status)}
                            color={getStatusColor(chitalishte.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          <Tooltip title="Виж детайли">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(chitalishte)}
                              color="primary"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            aria-label="actions"
                            aria-controls="actions-menu"
                            aria-haspopup="true"
                            onClick={(event) =>
                              handleMenuClick(event, chitalishte)
                            }
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    </Fade>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Menu
              id="actions-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              keepMounted
              sx={{ "& .MuiPaper-root": { minWidth: 200 } }}
            >
              <MenuItem
                onClick={() => {
                  handleEdit(selectedChitalishteForMenu);
                  handleMenuClose();
                }}
              >
                <Edit sx={{ mr: 1 }} /> Редактирай читалище
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleInformationCards(selectedChitalishteForMenu);
                  handleMenuClose();
                }}
              >
                <Assignment sx={{ mr: 1 }} /> Информационни карти
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleEditLibrary(selectedChitalishteForMenu);
                  handleMenuClose();
                }}
              >
                <LibraryBooks sx={{ mr: 1 }} /> Редактирай библиотека
              </MenuItem>
            </Menu>
          </Box>

          {totalPages > 1 && (
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 2 }}
            >
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </Box>
          )}

          {filteredChitalishta.length === 0 && !loading && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Groups sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {allChitalishta.length === 0
                  ? "Няма намерени читалища"
                  : "Няма резултати за текущите филтри"}
              </Typography>
              <Button
                startIcon={<Add />}
                onClick={handleCreate}
                variant="contained"
                sx={{ mr: 2 }}
              >
                Добави първото читалище
              </Button>
              {(searchTerm ||
                regionFilter ||
                municipalityFilter ||
                townFilter ||
                statusFilter) && (
                <Button
                  onClick={() => {
                    setSearchTerm("");
                    setRegionFilter("");
                    setMunicipalityFilter("");
                    setTownFilter("");
                    setStatusFilter("");
                    setCurrentPage(1);
                  }}
                  variant="outlined"
                >
                  Изчисти филтри
                </Button>
              )}
            </Box>
          )}

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}
        </>
      )}

      <ChitalishteForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        chitalishte={selectedChitalishte}
        onSubmit={handleSubmit}
        feedbackId={feedbackId}
        onEditLibrary={handleOpenLibraryFormFromChitalishte}
        fullScreen={isMobile}
      />

      {libraryFormOpen && (
        <LibraryForm
          open={libraryFormOpen}
          onClose={() => {
            setLibraryFormOpen(false);
            dispatch(clearLibraries());
          }}
          library={libraries[0]}
          chitalishteId={selectedChitalishteForLibrary.id}
          feedbackId={feedbackId}
          onSubmit={handleLibrarySubmit}
        />
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteFeedbackId("");
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle color="error">Потвърждение за изтриване</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Сигурни ли сте, че искате да изтриете читалище{" "}
            <strong>{chitalishteToDelete?.name}</strong>? Това действие е
            необратимо.
          </Typography>

          <TextField
            fullWidth
            label="Feedback ID (опционално)"
            value={deleteFeedbackId}
            onChange={(e) => setDeleteFeedbackId(e.target.value)}
            helperText="Свържи изтриването с конкретен feedback (UUID формат)"
            placeholder="напр. 123e4567-e89b-12d3-a456-426614174000"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteFeedbackId("");
            }}
          >
            Отказ
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Изтрий
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChitalishteAdmin;
